/**
 * Every URL that can carry a completed Stripe checkout session.
 *
 * | Param                      | Where it appears                                |
 * | -------------------------- | ----------------------------------------------- |
 * | `session_id`               | Stripe return (all order-confirmation pages)    |
 * | `original_product_session` | `/[locale]/[crossSellProduct]/offer`            |
 * | `previous_session_id`      | Order confirmation after a one-click cross-sell |
 * | `checkout_id`              | Intent checkout reference, on every URL above   |
 */
import { type CheckoutCountry, CHECKOUT_COUNTRIES } from './types'

export const PURCHASE_SESSION_PARAM_KEYS = ['previous_session_id', 'session_id', 'original_product_session'] as const

const COUNTRY_CODE_PARAM = 'country_code' as const

/** Session ids on the URL, earliest first. The same id can appear under two param names. */
export const extractPurchaseSessionIds = (params: URLSearchParams | null | undefined): string[] => {
  if (!params) return []
  // `previous_session_id` repeats when cross-sell offers are chained.
  const ids = PURCHASE_SESSION_PARAM_KEYS.flatMap((key) => params.getAll(key).map((id) => id.trim())).filter(
    (id) => id.length > 0,
  )
  return Array.from(new Set(ids))
}

const CHECKOUT_ID_PARAM = 'checkout_id' as const

/** The reference stands in for the first purchase's session id; the session ids are cross-sell purchases after it. */
export type PurchaseReference = { checkoutId: string | null; sessionIds: string[] }

export const extractCheckoutId = (params: URLSearchParams | null | undefined): string | null =>
  params?.get(CHECKOUT_ID_PARAM)?.trim() || null

export const extractPurchaseReference = (params: URLSearchParams | null | undefined): PurchaseReference => ({
  checkoutId: extractCheckoutId(params),
  sessionIds: extractPurchaseSessionIds(params),
})

/** One purchase named on a post-payment URL: an intent checkout reference or a Stripe checkout session. */
export type Purchase = { kind: 'intent' | 'session'; id: string }

/** Every purchase on the URL in the order made: the reference first, then the cross-sell sessions after it. */
export const extractPurchases = (params: URLSearchParams | null | undefined): Purchase[] => {
  const { checkoutId, sessionIds } = extractPurchaseReference(params)
  const sessions = sessionIds.map((id): Purchase => ({ kind: 'session', id }))
  return checkoutId ? [{ kind: 'intent', id: checkoutId }, ...sessions] : sessions
}

const isCheckoutCountry = (value: string): value is CheckoutCountry =>
  (CHECKOUT_COUNTRIES as readonly string[]).includes(value)

/** `country_code` is always on these routes; the locale is a fallback for hand-built links. */
export const resolvePurchaseCountryCode = (
  params: URLSearchParams | null | undefined,
  localeCountryCode?: string,
): CheckoutCountry | null => {
  const fromParam = params?.get(COUNTRY_CODE_PARAM)?.trim().toUpperCase()
  if (fromParam && isCheckoutCountry(fromParam)) return fromParam
  const fromLocale = localeCountryCode?.trim().toUpperCase()
  if (fromLocale && isCheckoutCountry(fromLocale)) return fromLocale
  return null
}
